import React, { useEffect, useRef, useState } from "react";
import "./ProgrammeStdMenuHideOnMinimal.css";
import { useGetLink } from "../../utils/useOpenLink";
import CustomLink from "../../utils/customLink";
import clsx from "clsx";
import useCustomTranslation from "../../utils/useCustomTranslation";
import { useSelector } from "react-redux";
import { useOpenMenu } from "../../utils/useOpenMenu";

function ProgrammeStdMenuHideOnMinimal({ contents: contentsArray = [], colors: colorObj }) {
    const { getLink } = useGetLink();
    const { t } = useCustomTranslation();
    const isMinimal = useSelector((state) => state.layout.isMinimal);

    const {
        menuRef,
        activeIndex,
        handleMouseEnter: mouseHover,
        handleMouseLeave,
    } = useOpenMenu(true, 200);

    const [collapsedIndexes, setCollapsedIndexes] = useState([]);
    const [dropdownInfo, setDropdownInfo] = useState(null);
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);

    const itemRefs = useRef([]);
    const menuWrapperRef = useRef(null);

    const handleToggleCollapse = (index) => {
        setCollapsedIndexes((prevState) =>
            prevState.includes(index)
                ? prevState.filter((i) => i !== index)
                : [...prevState, index],
        );
    };

    const MenuWrapper = ({ link, children }) =>
        link ? <CustomLink to={getLink(link)}>{children}</CustomLink> : <div>{children}</div>;

    const handleMouseEnter = (index) => {
        mouseHover(index);
    };

    useEffect(() => {
        if (activeIndex === null || !itemRefs.current[activeIndex]) {
            setDropdownInfo(null);
            return;
        }

        const rect = itemRefs.current[activeIndex].getBoundingClientRect();

        setDropdownInfo({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
            items: contentsArray[activeIndex]?.contents || [],
        });
    }, [activeIndex, contentsArray]);

    if (isMinimal) return null;

    return (
        <section
            className="prog-minimal-only-wrapper"
            style={{
                backgroundColor: colorObj?.background_color,
                borderTop: `1px solid ${colorObj?.accent_color2}`,
                borderBottom: `1px solid ${colorObj?.accent_color2}`,
            }}
        >
            <div className="container">
                <div className="row">
                    <div className="col-12 p-0">
                        <div className="relative">
                            <div className="menu-hover-wrapper" onMouseLeave={handleMouseLeave}>
                                <div className="scroll-container">
                                    {contentsArray.map((value, index) => (
                                        <div
                                            key={value.heading}
                                            className="item-wrapper cursor-pointer"
                                            ref={(el) => (itemRefs.current[index] = el)}
                                            onMouseEnter={() =>
                                                value.contents?.length && handleMouseEnter(index)
                                            }
                                        >
                                            <MenuWrapper
                                                link={
                                                    !value.contents?.length
                                                        ? value.content_reference
                                                        : null
                                                }
                                            >
                                                <div
                                                    className="item"
                                                    style={{
                                                        color: colorObj?.primary_color,
                                                        "--link-hover-color":
                                                            colorObj?.primary_color,
                                                        "--primary_color": colorObj?.primary_color,
                                                        "--accent_color3": colorObj?.accent_color3,
                                                        "--accent_color4": colorObj?.accent_color4,
                                                    }}
                                                >
                                                    {value.heading}
                                                    {value.contents?.length > 0 && (
                                                        <i
                                                            className={`pi ${
                                                                activeIndex === index
                                                                    ? "pi-angle-up"
                                                                    : "pi-angle-down"
                                                            } ltr:ml-2 rtl:mr-2`}
                                                        ></i>
                                                    )}
                                                </div>
                                            </MenuWrapper>
                                        </div>
                                    ))}
                                </div>

                                {activeIndex !== null && dropdownInfo?.items?.length > 0 && (
                                    <div
                                        ref={menuRef}
                                        className="dropdown fixed-dropdown"
                                        style={{
                                            position: "fixed",
                                            top: dropdownInfo.top,
                                            left: dropdownInfo.left,
                                            minWidth: dropdownInfo.width,
                                            "--primary_color": colorObj?.primary_color,
                                            "--accent_color2": colorObj?.accent_color2,
                                            "--accent_color3": colorObj?.accent_color3,
                                            "--accent_color4": colorObj?.accent_color4,
                                            "--background_color": colorObj?.background_color,
                                            zIndex: 2000,
                                        }}
                                    >
                                        {dropdownInfo.items.map((sub, i) => (
                                            <CustomLink to={getLink(sub.content_reference)} key={i}>
                                                <div className="dropdown-item">{sub.heading}</div>
                                            </CustomLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Mobile Menu */}

                        <div ref={menuWrapperRef} className="mobile-std-menu">
                            <div
                                className="menu-toggle cursor-pointer border-t border-b py-3 flex justify-between items-center"
                                onClick={() => setIsMenuCollapsed((prev) => !prev)}
                                style={{
                                    color: colorObj?.primary_color,
                                    "--primary_color": colorObj?.primary_color,
                                }}
                            >
                                <span className="text-sm tracking-widest font-medium">
                                    {t("constantString.menu_text")}
                                </span>
                                <i
                                    className={`pi ${
                                        isMenuCollapsed ? "pi-angle-up" : "pi-angle-down"
                                    }`}
                                />
                            </div>

                            {isMenuCollapsed && (
                                <div className="navMainWrapper">
                                    {contentsArray.map((value, index) => (
                                        <span
                                            key={value.heading}
                                            className="nav-item cursor-pointer"
                                            onClick={() => handleToggleCollapse(index)}
                                        >
                                            <MenuWrapper
                                                link={
                                                    !value.contents?.length
                                                        ? value.content_reference
                                                        : null
                                                }
                                            >
                                                <span
                                                    className={
                                                        collapsedIndexes?.includes(index)
                                                            ? "active"
                                                            : ""
                                                    }
                                                    style={{
                                                        color: colorObj?.primary_color,
                                                        "--link-hover-color":
                                                            colorObj?.primary_color,
                                                        "--primary_color": colorObj?.primary_color,
                                                    }}
                                                >
                                                    {value.heading}
                                                    {value.contents?.length > 0 && (
                                                        <i
                                                            className={`${
                                                                collapsedIndexes?.includes(index)
                                                                    ? "pi pi-angle-up ltr:ml-2 rtl:mr-2"
                                                                    : "pi angle-down pi-angle-down ltr:ml-2 rtl:mr-2"
                                                            }`}
                                                        ></i>
                                                    )}
                                                </span>
                                            </MenuWrapper>

                                            {value.contents?.length > 0 && (
                                                <ul
                                                    className={clsx(
                                                        "dropdown-menu",
                                                        collapsedIndexes?.includes(index)
                                                            ? "block"
                                                            : "hidden",
                                                    )}
                                                    style={{
                                                        "--primary_color": colorObj?.primary_color,
                                                        "--accent_color2": colorObj?.accent_color2,
                                                    }}
                                                >
                                                    {value.contents.map((subValue, i) => (
                                                        <li key={i}>
                                                            <CustomLink
                                                                to={getLink(
                                                                    subValue.content_reference,
                                                                )}
                                                                type={subValue.type}
                                                                key={i}
                                                            >
                                                                <div className="dropdown-item">
                                                                    {subValue.heading}
                                                                </div>
                                                            </CustomLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ProgrammeStdMenuHideOnMinimal;
